/**
 * Preview-session identity/persistence helpers, extracted out of Canvas.jsx
 * (module scope, no closure over component state — pure functions plus
 * localStorage reads/writes). Used by the live-preview render/trigger logic
 * (loadServicePreview and friends) to identify and resume a container-live
 * build session across page reloads.
 */

import { getAuthorizedFetch } from './fetchRegistry';
import { getBffHeaders } from './apiHeaders';
import config from '../config';
import { devLog } from './devLog';
import { backoffDelay, classifyStatusResponse, PreviewStatusError } from './previewReadiness';
import { buildSessionId } from './previewSessionId';

export { buildSessionId };

/**
 * Matches the preview-runner's own `VfsError` messages for "there is
 * nothing to build yet" (see container-live/preview-runner's `vfs.rs`):
 * a service folder vfs-service has never had a file written to
 * (`vfs-service tree lookup failed (404) for ...`, vfs-service's own
 * "Path does not exist" 404 on `/tree`) or one whose folder exists but is
 * genuinely empty (`vfs-service returned no files for project X / service
 * Y`). This is NOT a real build/compile failure — it's the normal state of
 * a service the agent (or the user) hasn't written any source into yet
 * (e.g. `services/<name>` right after it's created/selected, before the
 * first `vfs_write_file`) — so callers should route it to a friendly empty
 * state instead of the generic build-error UI.
 */
const EMPTY_SERVICE_ERROR_PATTERNS = [
  /vfs-service returned no files for project/i,
  /vfs-service tree lookup failed \(404\)/i,
  // A service folder that exists but has nothing buildable in it (e.g. only
  // the `.gitkeep` CreateServiceModal.jsx writes to materialize the folder)
  // doesn't 404 on the vfs pull — the pull succeeds with that one file, and
  // the preview-runner's own driver-detection is what reports there's
  // nothing to build. Canvas.jsx's own vfsClient.isServiceEmpty pre-check
  // should normally catch this BEFORE a render is even triggered; this
  // pattern is a defense-in-depth fallback in case that pre-check is ever
  // bypassed (e.g. a stale tab retried through a path that skips it).
  /driver could not detect a supported project/i,
];

export const isEmptyServiceError = (message) => (
  typeof message === 'string' && EMPTY_SERVICE_ERROR_PATTERNS.some((pattern) => pattern.test(message))
);

/**
 * Get userId from localStorage for a given project-service combination
 * @param {string} projectId - Project ID
 * @param {string} serviceName - Service name
 * @returns {string|null} - Stored userId or null if not found
 */
export const getUserIdFromStorage = (projectId, serviceName) => {
  try {
    const serviceKey = projectId ? `${projectId}-${serviceName}` : serviceName;
    const storageKey = `preview-userId-${serviceKey}`;
    return localStorage.getItem(storageKey);
  } catch (error) {
    console.error('Error reading userId from localStorage:', error);
    return null;
  }
};

/**
 * Save userId to localStorage for a given project-service combination
 * @param {string} projectId - Project ID
 * @param {string} serviceName - Service name
 * @param {string} userId - User ID to store
 */
export const saveUserIdToStorage = (projectId, serviceName, userId) => {
  try {
    const serviceKey = projectId ? `${projectId}-${serviceName}` : serviceName;
    const storageKey = `preview-userId-${serviceKey}`;
    localStorage.setItem(storageKey, userId);
    // eslint-disable-next-line no-console
    devLog(`💾 Saved userId to localStorage: ${storageKey} = ${userId}`);
  } catch (error) {
    console.error('Error saving userId to localStorage:', error);
  }
};

/**
 * Get or create a stable userId for a project-service combination
 * Uses localStorage to persist across page reloads
 * @param {string} projectId - Project ID
 * @param {string} serviceName - Service name
 * @returns {string} - Existing or newly generated userId
 */
export const getOrCreateUserId = (projectId, serviceName) => {
  // Try to get existing userId from localStorage
  let userId = getUserIdFromStorage(projectId, serviceName);

  if (!userId) {
    // Generate new userId if not found
    userId = crypto.randomUUID();
    saveUserIdToStorage(projectId, serviceName, userId);
    // eslint-disable-next-line no-console
    devLog(`🔑 Generated new User ID for ${projectId || 'no-project'}-${serviceName}: ${userId}`);
  } else {
    // eslint-disable-next-line no-console
    devLog(`♻️  Reusing stored User ID for ${projectId || 'no-project'}-${serviceName}: ${userId}`);
  }

  return userId;
};

/**
 * The runner's session id for a service (see previewSessionId.js): `<project>-<service, "/" -> "-">`. The tenant
 * and, with a project, the user are not part of it; kept as parameters for the callers that pass them.
 * @param {string} _tenantId - unused
 * @param {string} projectId - Project ID
 * @param {string} serviceName - Service name (`services/app`)
 * @param {string} userId - User ID (only without a project)
 * @returns {string} - Session ID
 */
export const generateSessionId = (_tenantId, projectId, serviceName, userId) => (
  serviceName ? buildSessionId(projectId, serviceName, userId) : 'default'
);

/**
 * Get currently selected session from localStorage
 * @returns {Object|null} - Selected session data or null
 */
export const getSelectedSession = () => {
  try {
    const sessionData = localStorage.getItem('mia_selectedSession');
    if (sessionData) {
      return JSON.parse(sessionData);
    }
    return null;
  } catch (error) {
    console.error('Error reading selected session from localStorage:', error);
    return null;
  }
};

/**
 * Save currently selected session to localStorage
 * @param {Object} sessionData - Session data to store
 */
export const setSelectedSession = (sessionData) => {
  try {
    localStorage.setItem('mia_selectedSession', JSON.stringify(sessionData));
    devLog('💾 Saved selected session:', sessionData);
  } catch (error) {
    console.error('Error saving selected session to localStorage:', error);
  }
};

/**
 * Get complete session data from localStorage
 * @param {string} projectId - Project ID
 * @param {string} serviceName - Service name
 * @param {string} userId - User ID
 * @returns {Object|null} - Session data {sessionId, previewUrl, containerPort, language} or null
 */
export const getSessionFromStorage = (projectId, serviceName, userId) => {
  try {
    const sessionId = projectId ? `${projectId}-${serviceName}-${userId}` : `${serviceName}-${userId}`;
    const storageKey = `preview-session-${sessionId}`;
    const sessionData = localStorage.getItem(storageKey);
    if (sessionData) {
      const session = JSON.parse(sessionData);
      // A session saved by an older build may carry an id the runner never had (`<project>-services/app-<uuid>`):
      // with a project the id is always `<project>-<service, "/" -> "-">`.
      if (session && projectId && serviceName) { session.sessionId = buildSessionId(projectId, serviceName); }
      return session;
    }
    return null;
  } catch (error) {
    console.error('Error reading session from localStorage:', error);
    return null;
  }
};

/**
 * Clear all preview-session-* keys from localStorage
 * Used when switching projects to avoid confusion with stale session data
 * Note: This does NOT terminate the actual render sessions - they remain alive
 * so users can reuse them when returning to a project
 */
export const clearAllPreviewSessions = () => {
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('preview-session-')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    // eslint-disable-next-line no-console
    devLog(`🧹 Cleared ${keysToRemove.length} preview session(s) from localStorage`);
  } catch (error) {
    console.error('Error clearing preview sessions:', error);
  }
};

/**
 * Save complete session data to localStorage
 * @param {string} projectId - Project ID
 * @param {string} serviceName - Service name
 * @param {string} userId - User ID
 * @param {Object} sessionData - Session data to store {sessionId, previewUrl, containerPort, language}
 */
export const saveSessionToStorage = (projectId, serviceName, userId, sessionData) => {
  try {
    const sessionId = projectId ? `${projectId}-${serviceName}-${userId}` : `${serviceName}-${userId}`;
    const storageKey = `preview-session-${sessionId}`;
    localStorage.setItem(storageKey, JSON.stringify(sessionData));
    // eslint-disable-next-line no-console
    devLog(`💾 Saved session to localStorage: ${storageKey}`, sessionData);

    // Also update mia_selectedSession to track the currently active session
    setSelectedSession({
      ...sessionData,
      projectId,
      serviceName,
      userId,
      lastAccessed: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error saving session to localStorage:', error);
  }
};

/**
 * Helper to determine if running in production environment
 * @returns {boolean} - true if in production
 */
export const isProduction = () => {
  if (typeof window === 'undefined' || !window.location.origin) return false;
  // A built app is served behind the api-gateway even on localhost (kind, docker compose): only the Vite dev server
  // has the /universal proxy. The hostname alone used to decide, so a local cluster posted to /universal/api/render.
  if (import.meta.env?.PROD) return true;
  return !window.location.origin.includes('localhost') &&
         !window.location.origin.includes('127.0.0.1');
};

/**
 * Get the container API URL (direct or through BFF proxy)
 * @param {number} containerPort - Container port (3001 for react, 3011 for node, etc.)
 * @param {string} endpoint - API endpoint path (e.g., '/api/render')
 * @returns {string} - Full API URL
 */
export const getContainerApiUrl = (containerPort, endpoint) => {
  // Check if K8s Dynamic Mode is enabled (default: false)
  const k8sDynamicMode = localStorage.getItem('k8sDynamicMode') === 'true';

  // Every stack routes through the single universal Rust preview-runner
  // (container-live/preview-runner), which auto-detects the stack from the
  // project's actual files — there's no per-port/per-stack container to
  // select between anymore (the crud-service-universal generator that used
  // to be the one exception has been decommissioned).
  const containerName = 'universal';

  if (isProduction()) {
    // K8s Dynamic Mode: Use /api/bff/preview/* endpoints
    if (k8sDynamicMode) {
      return `${config.bffUrl}/preview${endpoint}`;
    }

    // Single Container Mode (Default): Use container-specific endpoints
    // React container uses direct public URL, others use BFF proxy
    //if (containerPort === 3001) {
    // Direct access to the universal preview-runner via public URL
    const publicBaseUrl = config.publicBaseUrl || window.location.origin;
    return `${publicBaseUrl}/api/preview/${containerName}${endpoint}`;
    //}

    // All other containers use BFF proxy
    //return `${config.bffUrl}/${containerName}${endpoint}`;
  }

  // In development
  if (k8sDynamicMode) {
    // K8s Dynamic Mode: Use BFF preview endpoints
    return `http://localhost:3100/api/bff/preview${endpoint}`;
  }

  // Single Container Mode: Use direct localhost access
  return `/${containerName}${endpoint}`;
};

/**
 * Poll session build status until completion.
 *
 * Never spins on an answer it cannot read: a 3xx, 401/403 or a page instead of JSON (a gateway redirect to Flow's
 * own HTML or to the sign-in) is retried once more, then rejects with a PreviewStatusError (`code: 'signed_out'`) so
 * the Preview shows "could not reach the preview" with Retry. A network error or a 5xx is retried with backoff until
 * `maxWaitTime`. A 404 (the session not registered yet) is retried a few times.
 *
 * `options.isReady()`: the shared service state's view (Canvas passes the project's services list, the same one the
 * header reads): when it returns a ready session the poll resolves with it at once, whatever the poll itself saw.
 *
 * @param {string} containerApiBaseUrl - Base URL for container API
 * @param {string} sessionId - Session ID to poll
 * @param {Function} onProgress - Callback for progress updates (phase, progress, logs)
 * @param {number} maxWaitTime - Maximum time to wait in ms (default 7 minutes for large projects)
 * @param {{ isReady?: () => (object|null), sleep?: (ms: number) => Promise<void>, pollInterval?: number, now?: () => number }} [options]
 * @returns {Promise<Object>} - Final session result
 */
export const pollRenderStatus = async (containerApiBaseUrl, sessionId, onProgress, maxWaitTime = 420000, options = {}) => {
  const {
    isReady = null,
    sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
    pollInterval = 3000, // Poll every 3 seconds for faster updates
    now = Date.now,
  } = options;
  const startTime = now();
  let consecutiveStartingCount = 0; // Track how long we've been in 'starting' phase
  let lastProgress = 0;
  let failures = 0; // consecutive network errors / 5xx, for the backoff
  let signedOut = 0; // consecutive redirects / 401 / pages instead of JSON
  let notFound = 0; // consecutive 404s
  const MAX_SIGNED_OUT = 2;
  const MAX_NOT_FOUND = 5;

  const readyResult = () => {
    if (!isReady) { return null; }
    let ready = null;
    try { ready = isReady(); } catch { ready = null; }
    if (!ready || !ready.previewUrl) { return null; }
    devLog('✅ [pollRenderStatus] the service state reports the preview running:', ready.sessionId || sessionId);
    return { ...ready, previewUrl: ready.previewUrl, sessionId: ready.sessionId || sessionId, previewPort: ready.previewPort };
  };

  while (now() - startTime < maxWaitTime) {
    const fromState = readyResult();
    if (fromState) { return fromState; }

    const statusUrl = `${containerApiBaseUrl}/api/preview/${encodeURIComponent(sessionId)}/status`;
    let response;
    try {
      response = await getAuthorizedFetch()(statusUrl, {
        method: 'GET',
        // A redirect is never the runner's answer: see it rather than follow it to a page.
        redirect: 'manual',
        headers: getBffHeaders({ Accept: 'application/json' }),
      });
    } catch (error) {
      // Network error: try again with backoff (the runner restarting, a dropped connection).
      failures += 1;
      console.warn('⚠️ Preview status poll failed, retrying:', error?.message || error);
      await sleep(backoffDelay(failures));
      continue;
    }

    const kind = classifyStatusResponse(response);
    if (kind === 'signed-out') {
      signedOut += 1;
      if (signedOut >= MAX_SIGNED_OUT) {
        throw new PreviewStatusError(
          'Flow could not reach the preview: the status check was redirected or refused (your session may have expired).',
          'signed_out',
          Number(response.status) || null,
        );
      }
      // Once more after a pause: a refreshed token may get through.
      await sleep(backoffDelay(signedOut));
      continue;
    }
    if (kind === 'gone') {
      notFound += 1;
      if (notFound >= MAX_NOT_FOUND) {
        throw new Error('Status check failed: the preview session was not found');
      }
      await sleep(pollInterval);
      continue;
    }
    if (kind === 'retry') {
      failures += 1;
      await sleep(backoffDelay(failures));
      continue;
    }

    let data;
    try {
      data = await response.json();
    } catch {
      // Said JSON, was not: the same as a page instead of JSON.
      signedOut += 1;
      if (signedOut >= MAX_SIGNED_OUT) {
        throw new PreviewStatusError('Flow could not read the preview status (not JSON).', 'signed_out', Number(response.status) || null);
      }
      await sleep(backoffDelay(signedOut));
      continue;
    }
    failures = 0;
    signedOut = 0;
    notFound = 0;

    if (!data || !data.success || !data.session) {
      throw new Error(data?.message || 'Status check failed');
    }

    const session = data.session;

    // Debug: Log what we receive from backend
    devLog('[pollRenderStatus] session data:', {
      buildPhase: session.buildPhase,
      buildProgress: session.buildProgress,
      buildLogsCount: session.buildLogs?.length,
      buildLogs: session.buildLogs
    });

    // Call progress callback
    if (onProgress) {
      onProgress(session.buildPhase, session.buildProgress, session.buildLogs);
    }

    // Enhanced logging for transparency
    const phaseEmojis = {
      'initializing': '🎬',
      'copying': '📦',
      'installing': '📥',
      'starting': '🚀',
      'complete': '✅',
      'ready': '✅'
    };
    const emoji = phaseEmojis[session.buildPhase] || '⏳';
    // eslint-disable-next-line no-console
    devLog(`${emoji} [Preview] ${(session.buildPhase || 'unknown').toUpperCase()}: ${session.buildProgress}%`, session.buildLogs?.slice(-1)[0]?.message || '');

    const done = () => ({
      previewUrl: session.previewUrl,
      sessionId: session.sessionId,
      previewPort: session.previewPort,
      ...session
    });

    // FLEXIBLE COMPLETION DETECTION:
    // 1. Explicit completion status
    if (session.buildStatus === 'completed' || session.buildStatus === 'ready') {
      // eslint-disable-next-line no-console
      devLog('✅ Build completed with status:', session.buildStatus);
      return done();
    }

    // 2. Phase is 'complete' or 'ready'
    if ((session.buildPhase === 'complete' || session.buildPhase === 'ready') && session.buildProgress >= 100) {
      // eslint-disable-next-line no-console
      devLog('✅ Build completed with phase:', session.buildPhase);
      return done();
    }

    // Explicit failure or error.
    if (session.buildStatus === 'failed' || session.buildStatus === 'error' || session.buildPhase === 'error') {
      const errorMessage = session.buildError || 'Build failed';
      const buildError = new Error(errorMessage);
      // Tag "nothing to build yet" separately from a real build/compile
      // failure — see isEmptyServiceError's own docstring above. Callers
      // (Canvas.jsx) check this flag to show a friendly empty state
      // instead of the alarming build-error modal/card.
      buildError.isEmptyService = isEmptyServiceError(errorMessage);
      throw buildError;
    }

    // 3. The process runs with a preview URL and is no longer building: it is up.
    if (session.containerStatus === 'running' && !session.crashed && session.buildStatus !== 'building' && session.previewUrl) {
      devLog('✅ Process running with a preview URL:', session.buildStatus);
      return done();
    }

    // 4. Container is in 'starting' phase at high progress and has a preview URL
    // This indicates the server is running even if status isn't explicitly "complete"
    if (session.buildPhase === 'starting' && session.buildProgress >= 85 && session.previewUrl) {
      // Track how long we've been stuck at high progress
      if (session.buildProgress === lastProgress) {
        consecutiveStartingCount++;
      } else {
        consecutiveStartingCount = 0;
        lastProgress = session.buildProgress;
      }

      // If we've been stuck at 85%+ for 15+ seconds (5 polls), consider it ready
      if (consecutiveStartingCount >= 5) {
        // eslint-disable-next-line no-console
        devLog('✅ Assuming build complete - stuck at starting phase with preview URL for 15+ seconds');
        return done();
      }
    }

    // Wait before next poll
    await sleep(pollInterval);
  }

  // eslint-disable-next-line no-console
  console.error('⏱️ Render job timeout after', Math.floor(maxWaitTime / 1000), 'seconds');
  throw new Error(`Render job timeout - exceeded maximum wait time of ${Math.floor(maxWaitTime / 1000)} seconds. Large projects with 600+ files may require extended installation time (5-7 minutes). The build process may still be running in the background.`);
};

/**
 * The runner's session id behind a preview URL. Two URL shapes exist:
 *   - `/api/preview/<sessionId>/proxy[/…]`                  (the session id as one segment)
 *   - `/api/preview/<project>/<service path…>/proxy[/…]`   (the project and service as readable path;
 *                                                           the session id is `<project>-<service with / -> ->`)
 * Returns null when the URL is neither.
 * @param {string} url
 * @returns {string|null}
 */
export function sessionIdFromPreviewUrl(url) {
  if (!url) { return null; }
  const single = /\/api\/preview\/([^/]+)\/proxy(?:[/?#]|$)/.exec(url);
  if (single) { return decodeURIComponent(single[1]); }
  const readable = /\/api\/preview\/([^/]+)\/((?:[^/]+\/)+?)proxy(?:[/?#]|$)/.exec(url);
  if (!readable) { return null; }
  const service = readable[2].replace(/\/$/, '').split('/').map(decodeURIComponent).join('-');
  return `${decodeURIComponent(readable[1])}-${service}`;
}
